import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import differenceInSeconds from "date-fns/differenceInSeconds";
import ReactDOM from "react-dom";
import { ColorClassNames, ColorVariables } from "../Colors";
import { Cross } from "../Images/icons";
import { Button } from "../Button";
import { Container, FlexContainer, Spacing, ScrollContainer } from "../Containers";
import { baseLength, sm, md, lg, xl, getMobileBreakpoint } from "../Styles/globals";

// Common Sizes
const PopupWidthDesktop = 430;

const CenterAlign = styled.div<Partial<{ $showPopup: boolean }>>`
  position: absolute;
  display: ${(props) => (props.$showPopup ? "flex" : "none")};
  justify-content: center;
  align-items: center;
`;

const PopupOuterContainer = styled.div<Partial<{ $showPopup: boolean; $background: string }>>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1001;
  position: fixed;
  top: 0;
  left: 0;
  background-color: ${(props) => (props.$background === "green" ? "rgb(21, 111, 100)" : ColorVariables.orange)};
  opacity: 0.8;
  display: ${(props) => (props.$showPopup ? "flex" : "none")};
  justify-content: center;
  align-items: center;
`;

const PopupContainer = styled.div<
  Partial<{
    $showPopup: boolean;
    $paddingTop: number;
    $paddingRight: number;
    $paddingBottom: number;
    $paddingLeft: number;
    $width: string;
    $fullWidthInMobile: boolean;
  }>
>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${(props) => props.$width};
  background-color: ${ColorVariables.sugar};
  border-radius: ${sm}px;
  z-index: 1002;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(100% - 40px);

  overflow: hidden;
  box-shadow: 0px 4px 4px rgba(208, 204, 193, 0.6);
  padding-top: ${(props) => props.$paddingTop}px;
  padding-right: ${(props) => props.$paddingRight}px;
  padding-bottom: ${(props) => props.$paddingBottom}px;
  padding-left: ${(props) => props.$paddingLeft}px;

  @media (max-width: ${getMobileBreakpoint}) {
    ${(props) =>
      !props.$fullWidthInMobile
        ? `width: calc(100% - 40px);
    max-width: 330px;`
        : `width: calc(100% - 8px);
        max-width: 100vw;
        height: calc(100dvh - 10px);
        max-height: 100%;
        padding:0;
          box-shadow: none;
          
        `}
  }
`;

const ZohoBookingPopupContainer = styled(PopupContainer)`
  padding: 0;
`;

const ZohoBookingExitButton = styled.div`
  padding: 10px;
  display: flex;
  justify-content: flex-end;

  button {
    width: auto;
  }

  @media (min-width: 950px) {
    padding: 24px;
  }
`;

const ContactInfo = ({ phone = "", email = "" }) => {
  return (
    <>
      <Spacing $space={md} />
      <FlexContainer>
        <Spacing $space={md} />
        <FlexContainer marginLeft={md} $flexDirection="column">
          {phone && (
            <>
              <p className={`${ColorClassNames.mint} bold`}>{phone}</p>
              <Spacing $space={md} />
            </>
          )}
          {email && (
            <a href={email} rel="noopener noreferrer" target="_blank">
              <p className={`${ColorClassNames.mint} bold`}>{email}</p>
            </a>
          )}
        </FlexContainer>
      </FlexContainer>
    </>
  );
};

export type PopupProps = Partial<{
  closeOnBlur: boolean;
  showContactInfo: boolean;
  content: string | JSX.Element;
  showPopup: boolean;
  showHeader: boolean;
  handlePopup: (...args: any) => any;
  header: string;
  className: string;
  width: string;
  showClose: boolean;
  background: string;
  noScroll: boolean;
  disableScroll: boolean;
  fullWidthInMobile: boolean;
}>;

// Must have a #popup element defined somewhere in your app.tsx file to make this work
const Popup = ({
  closeOnBlur = true,
  showClose = true,
  width = `${PopupWidthDesktop}px`,
  showContactInfo = false,
  content = "",
  showPopup = true,
  handlePopup = undefined,
  header = "Here to help",
  showHeader = true,
  className = "",
  background = "",
  noScroll = false,
  disableScroll = false,
  fullWidthInMobile = false,
}: PopupProps) => {
  // TODO: Do not render this for unit test until we get an actual fix for Enzyme React Portal
  if (process.env.JEST_WORKER_ID) {
    return null;
  }

  const internalHandlePopup = useCallback(() => {
    if (closeOnBlur && handlePopup) return handlePopup();
  }, []);

  return ReactDOM.createPortal(
    <CenterAlign className={`popup help-card ${className}`} $showPopup={showPopup}>
      <PopupOuterContainer $showPopup={showPopup} $background={background} onClick={internalHandlePopup} />
      <PopupContainer
        $width={width}
        $paddingTop={xl}
        $paddingRight={xl}
        $paddingBottom={xl}
        $paddingLeft={xl}
        $fullWidthInMobile={fullWidthInMobile}
      >
        {showClose && (
          <FlexContainer className="fullWidth" height={lg} $justifyContent="flex-end" $alignItems="center">
            <Container cursor="pointer" $imageWidth={lg} $imageHeight={lg} onClick={handlePopup}>
              <Cross />
            </Container>
          </FlexContainer>
        )}
        {noScroll ? (
          <>
            {showHeader && (
              <>
                <Spacing $space={md} />
                <h4 className={ColorClassNames.mint}>{header}</h4>
                <Spacing $space={md} />
              </>
            )}
            {content}
            {showContactInfo && <ContactInfo email="support@usemanifest.com" />}
          </>
        ) : (
          <ScrollContainer $scrollable={!disableScroll}>
            {showHeader && (
              <>
                <Spacing $space={md} />
                <h4 className={ColorClassNames.mint}>{header}</h4>
                <Spacing $space={md} />
              </>
            )}
            {content}
            {showContactInfo && <ContactInfo email="support@usemanifest.com" />}
          </ScrollContainer>
        )}
      </PopupContainer>
    </CenterAlign>,
    document.querySelector("#popup")!
  );
};

export type PopupWithButtonProps = Partial<{
  closeOnBlur: boolean;
  showButtonOne: boolean;
  buttonOneText: string;
  buttonOneHandler: (...args: any) => any;
  showButtonTwo: boolean;
  buttonTwoText: string;
  buttonTwoHandler: (...args: any) => any;
  content: string | JSX.Element;
  header: string | JSX.Element;
  handlePopup: (...args: any) => any;
  showPopup: boolean;
  className: string;
  width: string;
  invert: boolean;
  showClose: boolean;
}>;

const PopupWithButton = ({
  width = `${PopupWidthDesktop}px`,
  closeOnBlur = true,
  showButtonOne = true,
  buttonOneText = "Confirm",
  buttonOneHandler = undefined,
  showButtonTwo = false,
  buttonTwoText = "Cancel",
  buttonTwoHandler = undefined,
  content = "If you cannot find your account provider through the search option, please connect your account provider manually.",
  header = "Confirm Manual Link",
  handlePopup = undefined,
  showPopup = false,
  className = "",
  invert = false,
  showClose = false,
}: PopupWithButtonProps) => {
  const internalHandlePopup = () => {
    if (closeOnBlur && handlePopup) return handlePopup();
  };

  return (
    <Container data-name={showPopup ? "popupCurrentlyShowing" : "popup"} className={`popup with-button ${className}`}>
      <CenterAlign $showPopup={showPopup}>
        <PopupOuterContainer $showPopup={showPopup} onClick={internalHandlePopup} />

        <PopupContainer $width={width} $paddingTop={xl} $paddingRight={xl} $paddingBottom={xl} $paddingLeft={xl}>
          {showClose && (
            <>
              <FlexContainer className="fullWidth" height={lg} $justifyContent="flex-end" $alignItems="center">
                <Container cursor="pointer" $imageWidth={lg} $imageHeight={lg} onClick={handlePopup}>
                  <Cross />
                </Container>
              </FlexContainer>
              <Spacing $space={10} />
            </>
          )}
          <FlexContainer $flexDirection="column">
            <h4 className={ColorClassNames.mint}>{header}</h4>
            <Spacing $space={md} />

            <p>{content}</p>

            {showButtonOne && (
              <>
                <Spacing $space={2 * xl} />
                <Button name="buttonOne" text={buttonOneText} inverted={invert} onClick={buttonOneHandler} />
              </>
            )}

            {showButtonTwo && (
              <>
                <Spacing $space={xl} />
                <Button name="buttonTwo" text={buttonTwoText} inverted={!invert} onClick={buttonTwoHandler} />
              </>
            )}
          </FlexContainer>
        </PopupContainer>
      </CenterAlign>
    </Container>
  );
};

export type TimeoutPopupProps = Partial<{
  closeOnBlur: boolean;
  buttonOneHandler: (...args: any) => any;
  buttonTwoHandler: (...args: any) => any;
  handlePopup: (...args: any) => any;
  showPopup: boolean;
  timeoutInSeconds: number;
  className: string;
}>;

const TimeoutPopup = ({
  closeOnBlur = true,
  buttonOneHandler,
  buttonTwoHandler,
  handlePopup,
  showPopup = false,
  timeoutInSeconds = 165,
  className = "",
}: TimeoutPopupProps) => {
  const [time, setTime] = useState(timeoutInSeconds);
  const [countdownStartDate, setCountdownStartDate] = useState<Date | null>(null);
  let countdownTimer = null as any;

  const internalHandlePopup = useCallback(() => {
    if (closeOnBlur && handlePopup) return handlePopup();
  }, []);

  const resume = useCallback(() => {
    if (buttonOneHandler) {
      buttonOneHandler();
    }
    clearInterval(countdownTimer);
  }, []);

  useEffect(() => {
    if (countdownStartDate === null && time > 0) {
      setCountdownStartDate(new Date());
    }
  }, [time]);

  useEffect(() => {
    if (!countdownStartDate) return;
    const countdownTime = time;
    countdownTimer = setInterval(() => {
      const newTime = countdownTime - differenceInSeconds(new Date(), countdownStartDate);
      if (newTime >= 0) {
        setTime(newTime);
      }
      if (newTime <= 0 && buttonTwoHandler) {
        buttonTwoHandler();
        setCountdownStartDate(null);
        clearInterval(countdownTimer);
      }
    }, 1000);
    return () => {
      clearInterval(countdownTimer);
    };
  }, [countdownStartDate]);

  return (
    <Container className={`popup timeout ${className}`}>
      <CenterAlign $showPopup={showPopup}>
        <PopupOuterContainer $showPopup={showPopup} onClick={internalHandlePopup} />

        <PopupContainer $paddingTop={xl} $paddingRight={xl} $paddingBottom={xl} $paddingLeft={xl}>
          <FlexContainer
            $flexDirection="column"
            $justifyContent="center"
            $alignItems="center"
            style={{ textAlign: "center" }}
          >
            <p className={`${ColorClassNames.mint} large bold`}>
              For your safety,
              <br /> this session will expire in:
            </p>
            <Spacing $space={7 * baseLength} $mobileSpace={xl} />

            <h1
              style={{
                color: ColorVariables.coco9,
              }}
            >
              {`${Math.floor(time / 60)}:${time % 60 < 10 ? "0" : ""}${time % 60}`}
            </h1>

            <Spacing $space={7 * baseLength} />

            <Button name="resume" text="Resume" onClick={resume} />

            <Spacing $space={xl} />
            <Button name="logOut" text="Log out" inverted onClick={buttonTwoHandler} />
          </FlexContainer>
        </PopupContainer>
      </CenterAlign>
    </Container>
  );
};

const ZohoBookingPopup = ({
  closeOnBlur = true,
  showPopup = true,
  handlePopup = undefined,
  content = "",
}: PopupProps) => {
  // TODO: Do not render this for unit test until we get an actual fix for Enzyme React Portal
  if (process.env.JEST_WORKER_ID) {
    return null;
  }

  const internalHandlePopup = useCallback(() => {
    if (closeOnBlur && handlePopup) return handlePopup();
  }, []);

  return ReactDOM.createPortal(
    <CenterAlign className="popup help-card" $showPopup={showPopup}>
      <PopupOuterContainer $showPopup={showPopup} onClick={internalHandlePopup} />
      <ZohoBookingPopupContainer
        $width="600px"
        $paddingTop={xl}
        $paddingRight={xl}
        $paddingBottom={xl}
        $paddingLeft={xl}
        $fullWidthInMobile
        style={{
          background: "#f3f3f3",
        }}
      >
        <ZohoBookingExitButton>
          <button onClick={handlePopup} data-name="exitBooking">
            Exit Booking
          </button>
        </ZohoBookingExitButton>

        <ScrollContainer $scrollable={false} $disabledPadding>
          {content}
        </ScrollContainer>
      </ZohoBookingPopupContainer>
    </CenterAlign>,
    document.querySelector("#popup")!
  );
};

export { Popup, PopupWithButton, TimeoutPopup, ZohoBookingPopup };
